export type ParsedWebp = {
  frames: ImageData[];
  delays: number[];
  width: number;
  height: number;
};

/**
 * ImageDecoder API를 사용하여 애니메이션 WebP의 모든 프레임을 디코딩한다.
 * @remotion/gif의 parse-generate.js 패턴을 따름.
 *
 * 각 프레임을 ImageData로 변환하여 배열에 저장하고,
 * 프레임별 delay(ms)를 함께 반환한다.
 */
export const parseWebp = async (
  src: string,
  signal?: AbortSignal,
): Promise<ParsedWebp> => {
  if (typeof ImageDecoder === 'undefined') {
    throw new Error(
      'ImageDecoder API is not available. Animated WebP requires a Chromium-based browser.',
    );
  }

  const response = await fetch(src, { signal });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch WebP: HTTP ${response.status} ${response.statusText} (src=${src.slice(0, 120)})`,
    );
  }

  // @remotion/gif 패턴: 전체 바이너리를 먼저 읽은 후 디코딩
  // ReadableStream 직접 전달 시 원격 URL 스트리밍 환경에서 디코딩 실패 가능
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength === 0) {
    throw new Error('Failed to fetch WebP: empty response body');
  }

  const decoder = new ImageDecoder({
    type: 'image/webp',
    data: buffer,
  });
  // ArrayBuffer 사용 시 tracks.ready를 먼저 대기해야 트랙 메타데이터가 준비됨
  // (ReadableStream과 달리 completed만으로는 tracks이 채워지지 않을 수 있음)
  await decoder.tracks.ready;

  const track = decoder.tracks.selectedTrack;
  if (!track) {
    decoder.close();
    throw new Error('No track found in WebP');
  }

  const frameCount = track.frameCount;
  const frames: ImageData[] = [];
  const delays: number[] = [];

  for (let i = 0; i < frameCount; i++) {
    const result = await decoder.decode({
      frameIndex: i,
      completeFramesOnly: true,
    });
    const videoFrame = result.image;

    // VideoFrame → ImageData 변환
    const canvas = new OffscreenCanvas(
      videoFrame.displayWidth,
      videoFrame.displayHeight,
    );
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      videoFrame.close();
      throw new Error('Failed to get 2d context from OffscreenCanvas');
    }

    ctx.drawImage(videoFrame, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    frames.push(imageData);
    // VideoFrame.duration은 microseconds 단위 → milliseconds로 변환
    // duration이 0, null, undefined이면 기본 100ms (10fps)
    // ?? 대신 || 사용: duration이 0인 경우도 fallback 적용
    const durationUs = videoFrame.duration || 100_000;
    delays.push(durationUs / 1000);
    videoFrame.close();
  }

  decoder.close();

  if (frames.length === 0) {
    throw new Error('No frames found in WebP');
  }

  return {
    frames,
    delays,
    width: frames[0]!.width,
    height: frames[0]!.height,
  };
};
